/**
 * AccessDeniedScreen Component
 * Displays appropriate error message when student cannot access course
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Alert
} from '@mui/material';
import {
  Block as BlockIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  Devices as DevicesIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const AccessDeniedScreen = ({ accessStatus, courseId }) => {
  const navigate = useNavigate();

  const getIcon = () => {
    switch (accessStatus?.reason) {
      case 'not_enrolled':
        return <LockIcon sx={{ fontSize: 80, color: 'warning.main' }} />;
      case 'expired':
        return <ScheduleIcon sx={{ fontSize: 80, color: 'error.main' }} />;
      case 'device_mismatch':
        return <DevicesIcon sx={{ fontSize: 80, color: 'info.main' }} />;
      case 'inactive_account':
        return <BlockIcon sx={{ fontSize: 80, color: 'error.main' }} />;
      default:
        return <ErrorIcon sx={{ fontSize: 80, color: 'error.main' }} />;
    }
  };

  const getTitle = () => {
    switch (accessStatus?.reason) {
      case 'not_enrolled':
        return 'Not Enrolled';
      case 'expired':
        return 'Course Expired';
      case 'device_mismatch':
        return 'Device Not Recognized';
      case 'inactive_account':
        return 'Account Inactive';
      case 'course_unpublished':
        return 'Course Not Available';
      default:
        return 'Access Denied';
    }
  };

  const getDescription = () => {
    return accessStatus?.message || 'You do not have access to this course';
  };

  const getSolution = () => {
    switch (accessStatus?.reason) {
      case 'not_enrolled':
        return 'Please contact admin to enroll in this course';
      case 'expired':
        return 'Your course subscription has ended. Contact admin to renew';
      case 'device_mismatch':
        return 'This course is bound to another device. Use the registered device or request a device reset';
      case 'inactive_account':
        return 'Your account has been deactivated. Please contact admin';
      case 'course_unpublished':
        return 'This course is still being prepared. Please check back later';
      default:
        return 'Please contact support for assistance';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3
          }}
        >
          <Box sx={{ mb: 3 }}>
            {getIcon()}
          </Box>

          <Typography variant="h4" fontWeight={700} gutterBottom>
            {getTitle()}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {getDescription()}
          </Typography>

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            {getSolution()}
          </Alert>

          {accessStatus?.details && (
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', textAlign: 'left' }}
            >
              <Typography variant="caption" color="text.secondary" component="div">
                <strong>Details:</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" component="pre" sx={{ fontSize: '0.75rem' }}>
                {JSON.stringify(accessStatus.details, null, 2)}
              </Typography>
            </Paper>
          )}

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate('/student/dashboard')}
            >
              Back to Dashboard
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/student/login')}
            >
              Logout
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default AccessDeniedScreen;
